import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { trainingApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  GraduationCap,
  Plus,
  BookOpen,
  Award,
  Calendar,
  Clock,
  Users,
  MapPin,
  CheckCircle2,
  Play,
  FileText,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  upcoming: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700'
};

const CATEGORY_COLORS = {
  technical: 'bg-purple-100 text-purple-700',
  soft_skills: 'bg-pink-100 text-pink-700',
  compliance: 'bg-amber-100 text-amber-700',
  leadership: 'bg-blue-100 text-blue-700'
};

const CERT_STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
  revoked: 'bg-gray-100 text-gray-700'
};

export default function TrainingPage() {
  const { user, canAccessHR, isEmployee } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [programForm, setProgramForm] = useState({
    title: '',
    description: '',
    category: 'technical',
    trainer: '',
    trainer_type: 'internal',
    start_date: '',
    end_date: '',
    duration_hours: 0,
    max_participants: 50,
    location: 'Online'
  });
  const [certForm, setCertForm] = useState({
    certification_name: '',
    issuing_authority: '',
    issue_date: '',
    expiry_date: '',
    credential_id: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterCategory]);

  const fetchData = async () => {
    try {
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const [programsRes, enrollmentsRes, certsRes] = await Promise.all([
        trainingApi.getPrograms(params),
        trainingApi.getEnrollments({}),
        trainingApi.getCertifications({})
      ]);
      setPrograms(programsRes.data);
      setEnrollments(enrollmentsRes.data);
      setCertifications(certsRes.data);
    } catch (error) {
      console.error('Failed to fetch training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      await trainingApi.createProgram({
        ...programForm,
        duration_hours: parseFloat(programForm.duration_hours),
        max_participants: parseInt(programForm.max_participants)
      });
      toast.success('Training program created successfully');
      setShowProgramModal(false);
      resetProgramForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create program');
    }
  };

  const handleEnroll = async (program) => {
    try {
      await trainingApi.enroll({
        training_id: program.id,
        training_title: program.title,
        employee_id: user?.employee_id || user?.id,
        employee_name: user?.name
      });
      toast.success('Successfully enrolled in training');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    }
  };

  const handleAddCertification = async (e) => {
    e.preventDefault();
    try {
      await trainingApi.addCertification({
        ...certForm,
        employee_id: user?.employee_id || user?.id,
        employee_name: user?.name
      });
      toast.success('Certification added successfully');
      setShowCertModal(false);
      resetCertForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add certification');
    }
  };

  const resetProgramForm = () => {
    setProgramForm({
      title: '',
      description: '',
      category: 'technical',
      trainer: '',
      trainer_type: 'internal',
      start_date: '',
      end_date: '',
      duration_hours: 0,
      max_participants: 50,
      location: 'Online'
    });
  };

  const resetCertForm = () => {
    setCertForm({
      certification_name: '',
      issuing_authority: '',
      issue_date: '',
      expiry_date: '',
      credential_id: ''
    });
  };

  const isEnrolled = (programId) => {
    return enrollments.some(e => e.training_id === programId);
  };

  const filteredPrograms = programs.filter(p =>
    !searchTerm || p.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const upcomingPrograms = programs.filter(p => p.status === 'upcoming').length;
  const myEnrollments = enrollments.length;
  const activeCerts = certifications.filter(c => c.status === 'active').length;

  return (
    <div className="space-y-6" data-testid="training-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="page-title">Training & Development</h1>
          <p className="page-description">Enhance skills and track certifications</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setShowCertModal(true)}>
            <Award className="h-4 w-4" />
            Add Certification
          </Button>
          {canAccessHR && (
            <Button className="btn-primary gap-2" onClick={() => setShowProgramModal(true)} data-testid="create-program-btn">
              <Plus className="h-4 w-4" />
              Create Program
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Upcoming Programs</p>
                <p className="text-3xl font-bold text-blue-600">{upcomingPrograms}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">My Enrollments</p>
                <p className="text-3xl font-bold text-green-600">{myEnrollments}</p>
              </div>
              <BookOpen className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Certifications</p>
                <p className="text-3xl font-bold text-amber-600">{activeCerts}</p>
              </div>
              <Award className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="programs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="programs" className="gap-2">
            <GraduationCap className="h-4 w-4" />
            Training Programs
          </TabsTrigger>
          <TabsTrigger value="enrollments" className="gap-2">
            <BookOpen className="h-4 w-4" />
            My Enrollments
          </TabsTrigger>
          <TabsTrigger value="certifications" className="gap-2">
            <Award className="h-4 w-4" />
            Certifications
          </TabsTrigger>
        </TabsList>

        <TabsContent value="programs">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Available Programs</CardTitle>
                  <CardDescription>Enroll in training to develop new skills</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search programs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-36">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="soft_skills">Soft Skills</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                      <SelectItem value="leadership">Leadership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : filteredPrograms.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">No training programs available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filteredPrograms.map(program => (
                    <div key={program.id} className="p-4 border rounded-lg hover:border-[#1a365d]/50 transition-colors">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold">{program.title}</h3>
                            <div className="flex gap-2 mt-1">
                              <Badge className={STATUS_COLORS[program.status]}>{program.status}</Badge>
                              <Badge className={CATEGORY_COLORS[program.category]}>{program.category.replace('_', ' ')}</Badge>
                            </div>
                          </div>
                          {program.status === 'upcoming' && !isEnrolled(program.id) && (
                            <Button size="sm" className="btn-primary gap-1" onClick={() => handleEnroll(program)}>
                              <Play className="h-3 w-3" />
                              Enroll
                            </Button>
                          )}
                          {isEnrolled(program.id) && (
                            <Badge variant="secondary" className="gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Enrolled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{program.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(program.start_date).toLocaleDateString('en-IN')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {program.duration_hours}h
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {program.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {program.enrolled_count}/{program.max_participants}
                          </span>
                        </div>
                        <p className="text-sm">
                          <span className="text-muted-foreground">Trainer:</span>{' '}
                          <span className="font-medium">{program.trainer}</span>
                          <span className="text-muted-foreground"> ({program.trainer_type})</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="enrollments">
          <Card>
            <CardHeader>
              <CardTitle>My Enrollments</CardTitle>
              <CardDescription>Track your training progress</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="responsive-table-container">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Training</TableHead>
                      <TableHead>Enrolled On</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Certificate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrollments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12">
                          <BookOpen className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                          <p className="text-muted-foreground">No enrollments yet</p>
                          <p className="text-sm text-muted-foreground/75">Browse available programs and enroll</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      enrollments.map(enrollment => (
                        <TableRow key={enrollment.id}>
                          <TableCell className="font-medium">{enrollment.training_title}</TableCell>
                          <TableCell>{new Date(enrollment.enrolled_at).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell>
                            <Badge className={enrollment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                              {enrollment.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{enrollment.score ? `${enrollment.score}%` : '-'}</TableCell>
                          <TableCell>
                            {enrollment.certificate_url ? (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={enrollment.certificate_url} target="_blank" rel="noopener noreferrer">
                                  <FileText className="h-4 w-4" />
                                </a>
                              </Button>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certifications">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>My Certifications</CardTitle>
                  <CardDescription>Track your professional certifications</CardDescription>
                </div>
                <Button className="gap-2" onClick={() => setShowCertModal(true)}>
                  <Plus className="h-4 w-4" />
                  Add Certification
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="responsive-table-container">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Certification</TableHead>
                      <TableHead>Issuing Authority</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Credential ID</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {certifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <Award className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                          <p className="text-muted-foreground">No certifications added</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      certifications.map(cert => (
                        <TableRow key={cert.id}>
                          <TableCell className="font-medium">{cert.certification_name}</TableCell>
                          <TableCell>{cert.issuing_authority}</TableCell>
                          <TableCell>{new Date(cert.issue_date).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell>
                            {cert.expiry_date 
                              ? new Date(cert.expiry_date).toLocaleDateString('en-IN')
                              : 'No Expiry'}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{cert.credential_id || '-'}</TableCell>
                          <TableCell>
                            <Badge className={CERT_STATUS_COLORS[cert.status]}>{cert.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Program Modal */}
      <Dialog open={showProgramModal} onOpenChange={setShowProgramModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-[#1a365d]" />
              Create Training Program
            </DialogTitle>
            <DialogDescription>Set up a new training program for employees</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProgram} className="space-y-4">
            <div className="space-y-2">
              <Label>Program Title *</Label>
              <Input
                required
                value={programForm.title}
                onChange={(e) => setProgramForm({ ...programForm, title: e.target.value })}
                placeholder="e.g., Advanced React Development"
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                required
                rows={2}
                value={programForm.description}
                onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
                placeholder="Describe the training program..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={programForm.category}
                  onValueChange={(v) => setProgramForm({ ...programForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="soft_skills">Soft Skills</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="leadership">Leadership</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={programForm.location}
                  onChange={(e) => setProgramForm({ ...programForm, location: e.target.value })}
                  placeholder="e.g., Online, Conference Room A"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Trainer Name *</Label>
                <Input
                  required
                  value={programForm.trainer}
                  onChange={(e) => setProgramForm({ ...programForm, trainer: e.target.value })}
                  placeholder="Trainer name"
                />
              </div>
              <div className="space-y-2">
                <Label>Trainer Type</Label>
                <Select
                  value={programForm.trainer_type}
                  onValueChange={(v) => setProgramForm({ ...programForm, trainer_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  required
                  value={programForm.start_date}
                  onChange={(e) => setProgramForm({ ...programForm, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  required
                  value={programForm.end_date}
                  onChange={(e) => setProgramForm({ ...programForm, end_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (hours)</Label>
                <Input
                  type="number"
                  value={programForm.duration_hours}
                  onChange={(e) => setProgramForm({ ...programForm, duration_hours: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowProgramModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create Program
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Certification Modal */}
      <Dialog open={showCertModal} onOpenChange={setShowCertModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-[#1a365d]" />
              Add Certification
            </DialogTitle>
            <DialogDescription>Record a professional certification</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCertification} className="space-y-4">
            <div className="space-y-2">
              <Label>Certification Name *</Label>
              <Input
                required
                value={certForm.certification_name}
                onChange={(e) => setCertForm({ ...certForm, certification_name: e.target.value })}
                placeholder="e.g., AWS Solutions Architect"
              />
            </div>
            <div className="space-y-2">
              <Label>Issuing Authority *</Label>
              <Input
                required
                value={certForm.issuing_authority}
                onChange={(e) => setCertForm({ ...certForm, issuing_authority: e.target.value })}
                placeholder="e.g., Amazon Web Services"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Date *</Label>
                <Input
                  type="date"
                  required
                  value={certForm.issue_date}
                  onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={certForm.expiry_date}
                  onChange={(e) => setCertForm({ ...certForm, expiry_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Credential ID</Label>
              <Input
                value={certForm.credential_id}
                onChange={(e) => setCertForm({ ...certForm, credential_id: e.target.value })}
                placeholder="Optional credential ID"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCertModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Add Certification
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
